// src/components/admin/about/AdminAboutEditor.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; 
import fetchData from '../../../api/fetchData';
import { supportedLngs } from '../../../i18n';  
import { useTranslation } from 'react-i18next';

function AdminAboutEditor({ onContentUpdated }) { 
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); 
  const [allTranslations, setAllTranslations] = useState(null);

  const [imageUrl, setImageUrl] = useState('');
  const [editingLanguage, setEditingLanguage] = useState(i18n.language.split('-')[0] || 'ru');
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentParagraph, setCurrentParagraph] = useState('');
  const [currentListItem, setCurrentListItem] = useState('');
  
  // Технические состояния
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });

  // 1. Загрузка данных при первом отображении компонента
  useEffect(() => {
    const loadAboutData = async () => {
      setLoading(true);
      setError(null);
      try {
    
        const response = await fetchData('/admin/about/admin_get_about_data.php', 'GET', null, { credentials: 'include' });
        if (response && response.status === 'success' && response.item) {
          const fetchedItem = response.item;
          setImageUrl(fetchedItem.image_url || '');
          setAllTranslations(fetchedItem.translations || {});
        } else {
          setError(response?.message || 'Не удалось загрузить данные секции "Обо мне".');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadAboutData();
  }, []); 


  useEffect(() => {
    if (allTranslations) { 
      const langData = allTranslations[editingLanguage] || { title: '', paragraph: '', list_item: '' };
      setCurrentTitle(langData.title || '');
      setCurrentParagraph(langData.paragraph || '');
      setCurrentListItem(langData.list_item || '');
    }
  }, [editingLanguage, allTranslations]); 

  const handleLanguageTabClick = (langCode) => {
    setEditingLanguage(langCode);
  };

const handleSave = async (e) => {
  e.preventDefault();
  setIsSaving(true);
  setSaveStatus({ message: '', type: '' }); 

  const payload = {
    language_code: editingLanguage,
    title: currentTitle,
    paragraph: currentParagraph,
    list_item: currentListItem,
    image_url: imageUrl
  };

  
  try {
    const response = await fetchData(
      '/admin/about/admin_save_about_data.php', 
      'POST',
      payload,
      { credentials: 'include' }
    );

    if (response && response.status === 'success') {
      setSaveStatus({ message: response.message, type: 'success' });
      
      if (onContentUpdated) {
        onContentUpdated();
      }

    } else {
      setSaveStatus({ message: response?.message || t('admin.about.saveError'), type: 'error' });
    }
  } catch (err) {
    setSaveStatus({ message: t('messages.error.generic', { details: err.message }), type: 'error' });
  } finally {
    setIsSaving(false);
    setTimeout(() => setSaveStatus({ message: '', type: '' }), 5000);
  }
};

  if (loading) return <p>{t('messages.loading')}</p>;
  if (error) return <p>{t('messages.error.generic', { details: error })}</p>;

  return (
    <div className="admin-section-editor">
      <h4>{t('admin.about.managerTitle', 'Управление секцией "Обо мне"')}</h4>
      <div className="language-tabs">
    {supportedLngs.map(langCode => (
        <button
            key={langCode}
            type="button" // Важно для кнопок внутри формы, чтобы они не отправляли ее
            onClick={() => handleLanguageTabClick(langCode)}
            disabled={editingLanguage === langCode}
            className={editingLanguage === langCode ? 'active' : ''}
        >
            {langCode.toUpperCase()}
        </button>
    ))}
      </div>
      
      <form onSubmit={handleSave}>
        <fieldset>
          <legend>{t('admin.about.commonDataLegend', 'Непереводимые данные')}</legend>
          <div>
            <label htmlFor="aboutImageUrl">{t('admin.about.imageUrlLabel', 'URL изображения')}:</label>
            <input 
              type="text" 
              id="aboutImageUrl" 
              value={imageUrl} 
              onChange={e => setImageUrl(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </fieldset>

        <fieldset>
          <legend>{t('admin.about.translatableDataLegend', 'Переводимые данные для языка:')} {editingLanguage.toUpperCase()}</legend>
          <div>
            <label htmlFor="aboutTitle">{t('admin.about.titleLabel', 'Заголовок')}:</label>
            <input type="text" id="aboutTitle" value={currentTitle} onChange={e => setCurrentTitle(e.target.value)} />
          </div>
          <div>
            <label htmlFor="aboutParagraph">{t('admin.about.paragraphLabel', 'Параграф')}:</label>
            <textarea id="aboutParagraph" value={currentParagraph} onChange={e => setCurrentParagraph(e.target.value)} rows="5" />
          </div>
          <div>
            <label htmlFor="aboutListItem">{t('admin.about.listItemLabel', 'Список элементов (через ;)')}:</label>
            <textarea id="aboutListItem" value={currentListItem} onChange={e => setCurrentListItem(e.target.value)} rows="3" />
          </div>
        </fieldset>
        
        <button type="submit" disabled={isSaving} style={{ marginTop: '10px' }}>
          {isSaving ? t('admin.buttons.saving') : `${t('admin.buttons.save')} ${editingLanguage.toUpperCase()}`}
        </button>
      </form>

      {saveStatus.message && (
        <p className={`form-status-message ${saveStatus.type}`}>
          {saveStatus.message}
        </p>
      )}
    </div>
  );
}

export default AdminAboutEditor;